import React, { useState } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  Button,
  Grid,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const SearchAndFilter = ({ filters, onFilterChange, allTags }) => {
  const [localFilters, setLocalFilters] = useState({
    searchText: filters.searchText || '',
    category: filters.category || '',
    startDate: filters.startDate ? dayjs(filters.startDate) : null,
    endDate: filters.endDate ? dayjs(filters.endDate) : null,
    tags: filters.tags || []
  });

  const handleChange = (field, value) => {
    const newFilters = { ...localFilters, [field]: value };
    setLocalFilters(newFilters);
    
    // Convert dates for filter
    const filterData = {
      ...newFilters,
      startDate: newFilters.startDate ? newFilters.startDate.toDate() : null,
      endDate: newFilters.endDate ? newFilters.endDate.toDate() : null
    };
    onFilterChange(filterData);
  };

  const handleTagToggle = (tag) => {
    // Case-insensitive check
    const tagLower = tag.toLowerCase();
    const hasTag = localFilters.tags.some(t => t.toLowerCase() === tagLower);
    const newTags = hasTag
      ? localFilters.tags.filter(t => t.toLowerCase() !== tagLower)
      : [...localFilters.tags, tag];
    handleChange('tags', newTags);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      searchText: '',
      category: '',
      startDate: null,
      endDate: null,
      tags: []
    };
    setLocalFilters(clearedFilters);
    onFilterChange({
      ...clearedFilters,
      startDate: null,
      endDate: null
    });
  };

  const hasActiveFilters = 
    localFilters.searchText ||
    localFilters.category ||
    localFilters.startDate ||
    localFilters.endDate ||
    localFilters.tags.length > 0;

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Search"
            placeholder="Search events..."
            value={localFilters.searchText}
            onChange={(e) => handleChange('searchText', e.target.value)}
            size="small"
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Category</InputLabel>
            <Select
              value={localFilters.category}
              label="Category"
              onChange={(e) => handleChange('category', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="personal">Personal</MenuItem>
              <MenuItem value="work">Work</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={3}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              value={localFilters.startDate}
              onChange={(newValue) => handleChange('startDate', newValue)}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12} md={3}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="End Date"
              value={localFilters.endDate}
              onChange={(newValue) => handleChange('endDate', newValue)}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </LocalizationProvider>
        </Grid>

        {allTags.length > 0 && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 1 }}>
                Filter by tags:
              </Typography>
              {allTags.map((tag) => {
                const tagLower = tag.toLowerCase();
                const isSelected = localFilters.tags.some(t => t.toLowerCase() === tagLower);
                return (
                  <Chip
                    key={tag}
                    label={tag}
                    onClick={() => handleTagToggle(tag)}
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                    size="small"
                  />
                );
              })}
            </Box>
          </Grid>
        )}

        {hasActiveFilters && (
          <Grid item xs={12}>
            <Button onClick={handleClearFilters} size="small" variant="outlined">
              Clear Filters
            </Button>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default SearchAndFilter;

